"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { Menu, X, ChevronRight, Download } from "lucide-react";
import { GoldRateTicker } from "@/components/ui/GoldRateTicker";
import { DownloadAppLink } from "@/components/ui/DownloadAppLink";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Live Prices", href: "/gold-silver-prices" },
  { label: "About", href: "/about-us" },
  { label: "For Business", href: "/partner-with-us" },
  { label: "Blogs", href: "/blogs" },
  { label: "FAQ's", href: "/faqs" },
  { label: "Contact", href: "/contact-us" },
];

const easeStandard = [0.4, 0, 0.2, 1] as const;
const easeAccelerate = [0.4, 0, 1, 1] as const;
const easeEmphasized = [0.2, 0, 0, 1] as const;


export function NavBar() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(64);
  const headerRef = useRef<HTMLElement>(null);
  const shouldReduce = useReducedMotion();
  const desktopCtaClassName = buttonVariants({ variant: "brand", size: "sm" });
  const mobileCtaClassName = buttonVariants({ variant: "brand", size: "md" });

  // Detect scroll for blur/shadow effect
  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // Measure the header's real rendered height (it grows on xs screens, which
  // add an extra rate ticker row below the main nav bar) and publish it as a
  // CSS var so <main>'s top padding, this drawer, and any fixed elements on
  // other pages (e.g. the blog reading progress bar) can offset correctly
  // instead of assuming a fixed 64px.
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const apply = (height: number) => {
      setHeaderHeight(height);
      document.documentElement.style.setProperty(
        "--header-height",
        `${height}px`
      );
    };

    apply(el.offsetHeight);

    const observer = new ResizeObserver(([entry]) => {
      apply(entry.target.getBoundingClientRect().height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const drawerVariants: Variants = {
    hidden: { opacity: 0, y: shouldReduce ? 0 : -16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.25, ease: easeStandard } },
    exit: { opacity: 0, y: shouldReduce ? 0 : -8, transition: { duration: 0.18, ease: easeAccelerate } },
  };

  const linkItemVariants: Variants = {
    hidden: { opacity: 0, x: shouldReduce ? 0 : -16 },
    visible: (i: number) => ({
      opacity: 1, x: 0,
      transition: { delay: i * 0.06, duration: 0.22, ease: easeEmphasized },
    }),
  };

  return (
    <>
      <header
        id="global-header"
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-[200] transition-all duration-300 ${
          scrolled
            ? "bg-neutral-0/95 backdrop-blur-md shadow-sm border-b border-neutral-200"
            : "bg-neutral-0/80 backdrop-blur-sm"
        }`}
        style={{ backgroundColor: scrolled ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.85)" }}
      >
        <div className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-20">
          <div className="flex items-center justify-between h-16 lg:h-[72px]">

            {/* Logo */}
            <Link
              href="/"
              className="flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50 rounded-lg"
              aria-label="JustGold Home"
            >
              <Image
                src="/just-gold-logo.png"
                alt="JustGold logo"
                width={274}
                height={100}
                priority
                className="h-10 w-auto lg:h-10"
              />
            </Link>

            {/* Desktop nav links */}
            <nav className="hidden lg:flex items-center gap-1 mr-auto ml-8" aria-label="Main navigation">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                    className={`px-4 py-2 text-sm font-medium rounded-lg
                    hover:text-primary-600 hover:bg-primary-50/70
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40
                    transition-colors duration-200 ${pathname === link.href ? "bg-primary-50 text-primary-700" : "text-primary-800"}`}
                  aria-current={pathname === link.href ? "page" : undefined}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right side — rate ticker + CTA */}
            <div className="hidden lg:flex items-center gap-5">
              {/* Live rates */}
              <div className="hidden min-[1320px]:block">
                <GoldRateTicker />
              </div>
              {/* CTA */}
              <DownloadAppLink className={cn(desktopCtaClassName)}>
                <Download className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                Download App
              </DownloadAppLink>
            </div>

            {/* Mobile right: ticker + CTA + hamburger */}
            <div className="flex lg:hidden items-center gap-3">
              <div className="hidden sm:block">
                <GoldRateTicker />
              </div>
              <button
                onClick={() => setMobileOpen((o) => !o)}
                className="p-2.5 rounded-lg text-primary-800
                  hover:bg-primary-50 hover:text-primary-600
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/50
                  transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
                aria-controls="mobile-menu"
              >
                <AnimatePresence mode="wait" initial={false}>
                  {mobileOpen ? (
                    <motion.span
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      <X className="w-5 h-5" />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="open"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      <Menu className="w-5 h-5" />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile rate ticker bar (xs only) */}
        <div className="sm:hidden border-t border-neutral-100 px-4 py-2 flex justify-center">
          <GoldRateTicker />
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[190] bg-black/30 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer panel */}
            <motion.div
              id="mobile-menu"
              key="drawer"
              variants={drawerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              style={{ top: headerHeight }}
              className="fixed left-0 right-0 z-[195] lg:hidden
                bg-white border-b border-neutral-200 shadow-lg overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
            >
              <nav className="max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-20 py-6 flex flex-col gap-1">
                {NAV_LINKS.map((link, i) => (
                  <motion.div key={link.href} custom={i} variants={linkItemVariants} initial="hidden" animate="visible">
                    <Link
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center justify-between px-4 py-3.5 rounded-card-sm
                        text-primary-800 font-medium text-base
                        hover:bg-primary-50 hover:text-primary-600
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40
                        transition-colors duration-150 group"
                    >
                      {link.label}
                      <ChevronRight
                        className="w-4 h-4 text-primary-400 group-hover:text-primary-500
                          group-hover:translate-x-0.5 transition-transform duration-150"
                        aria-hidden="true"
                      />
                    </Link>
                  </motion.div>
                ))}

                {/* Divider */}
                <div className="my-2 h-px bg-neutral-100" />

                {/* Mobile CTA */}
                <motion.div
                  custom={NAV_LINKS.length}
                  variants={linkItemVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <DownloadAppLink
                    onClick={() => setMobileOpen(false)}
                    className={cn(mobileCtaClassName)}
                  >
                    Download App
                  </DownloadAppLink>
                </motion.div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Persistent mobile download action */}
      {!mobileOpen && (
        <DownloadAppLink
          className="fixed inset-x-4 bottom-4 z-[180] flex min-h-12 items-center justify-center gap-3 rounded-card bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-primary ring-1 ring-white/20 transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:hidden"
          aria-label="Download JustGold app"
        >
          <Download className="h-5 w-5" strokeWidth={2.2} aria-hidden="true" />
          <span>Download JustGold App</span>
        </DownloadAppLink>
      )}
    </>
  );
}
